import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { memo, useEffect, useMemo, useState } from 'react'
import { FlexAlignType, Image, Platform, Pressable, StyleSheet, Text } from 'react-native'
import { Snackbar } from 'react-native-paper'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { create } from 'zustand'

import { useStore } from '../models'
import { DS } from '../design-system'
import { useNetworkDetection } from '../models/network/useNetworkDetection.hook'
import { useNetInfo } from '@react-native-community/netinfo'

export const SNACK_TYPES = {
  AllIn: 'AllIn',
  GameDeleted: 'GameDeleted',
  GameStart: 'GameStart',
  GamePaused: 'GamePaused',
  GameResumed: 'GameResumed',
  GameFinished: 'GameFinished'
} as const

export const NetworkDownToast = observer(() => {
  useNetworkDetection()
  const netInfo = useNetInfo()

  const insets = useSafeAreaInsets()

  const dismiss = `We can't reach our network right now. Please check your connection.\n`
  const formattedNetInfoState = useMemo(
    () => Object.entries(netInfo).map(([key, value]) => `${key}: ${JSON.stringify(value)}`),
    [netInfo]
  )
  const message = `${dismiss}\n${formattedNetInfoState.join('\n')}`

  const [isNetworkDown, setIsNetworkDown] = useState(false)

  useEffect(() => {
    if (netInfo?.isConnected !== undefined) {
      setIsNetworkDown(Boolean(!netInfo?.isConnected))
    }
  }, [netInfo?.isConnected])

  if (!isNetworkDown) return null

  return (
    <Snackbar
      visible={isNetworkDown}
      duration={Number.POSITIVE_INFINITY}
      onDismiss={debounce(() => {
        setIsNetworkDown(false)
      })}
      action={{
        label: 'Dismiss',
        textColor: 'white'
      }}
      elevation={0}
      wrapperStyle={{
        paddingTop: Platform.OS === 'ios' ? insets.top - 12 : insets.top + 10,
        backgroundColor: '#d23a00',
        top: 0,
        height: 248
      }}
      theme={{ colors: { primary: '#fff' } }}
      style={{
        backgroundColor: 'transparent',
        height: 160,
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Text style={{ color: '#fff' }}>{message}</Text>
    </Snackbar>
  )
})

export const UserGameScheduleToast = observer(() => {
  const {
    game: { toast, setToast }
  } = useStore()

  if (!toast) return null

  const renderToastTypeStyle = () => {
    switch (toast.kind) {
      case 'success':
        return {
          top: 100,
          alignSelf: 'center' as FlexAlignType,
          display: 'flex' as const,
          justifyContent: 'center' as const,
          backgroundColor: 'white',
          borderRadius: 24,
          width: 240,
          height: 34,
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: 8
          },
          shadowOpacity: 0.46,
          shadowRadius: 11.14,
          elevation: 5
        }
      case 'Game Code':
        return {
          bottom: 120,
          alignSelf: 'center' as FlexAlignType,
          display: 'flex' as const,
          justifyContent: 'center' as const,
          backgroundColor: 'white',
          borderRadius: 50,
          paddingTop: 0,
          width: 139,
          paddingBottom: 0,
          height: 30,
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: 8
          },
          shadowOpacity: 0.46,
          shadowRadius: 11.14,
          elevation: 5
        }
      default:
        return {
          bottom: 120,
          alignSelf: 'center' as FlexAlignType,
          display: 'flex' as const,
          justifyContent: 'center' as const,
          backgroundColor: 'white',
          borderRadius: 50,
          width: 139,
          height: 34,
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: 8
          },
          shadowOpacity: 0.46,
          shadowRadius: 11.14,
          elevation: 5
        }
    }
  }

  const toastWrapperStyle = renderToastTypeStyle()

  return (
    <Snackbar
      visible={Boolean(toast)}
      duration={3000}
      onDismiss={debounce(() => setToast(null))}
      elevation={0}
      wrapperStyle={toastWrapperStyle}
      theme={{
        colors: { inverseSurface: '#fff', inverseOnSurface: '#000' }
      }}
      style={{
        backgroundColor: 'transparent',
        padding: 0,
        margin: 0
      }}
    >
      <DS.Frame row justifyContent="center" gap={'12'} style={toast.kind === 'success' && styles.textContainer}>
        <DS.Icon name="Check--enclosed" size={16} color="Utility/Success" />
        <DS.Text variant="Body Small/Medium">{toast.message}</DS.Text>
      </DS.Frame>
    </Snackbar>
  )
})

type ToastKind = 'success' | 'default'

type Toast = {
  id: string
  kind: ToastKind
  message: string
  duration?: number
  icon?: string
  remove: () => void
}

type ToastInput = Omit<Toast, 'id' | 'remove'>

type ToastStore = {
  queue: Map<string, Toast>
  enqueueToast: (toast: ToastInput) => void
  getQueue: () => Toast[]
}

export const useToastStore = create<ToastStore>((set, get) => ({
  queue: new Map(),
  enqueueToast: (toast: ToastInput) => {
    const toastId = Date.now().toString()
    const remove = () => {
      set((s) => {
        s.queue.delete(toastId)
        return {
          queue: s.queue
        }
      })
    }
    set((s) => {
      s.queue.set(toastId, { ...toast, id: toastId, remove })
      return {
        queue: s.queue
      }
    })
  },
  getQueue: () => Array.from(get().queue.values())
}))

export const Toast = memo(() => {
  const { getQueue } = useToastStore()
  const queue = getQueue()

  if (!queue) {
    return null
  }
  return queue.map((t) => <ToastItem key={t.id} toast={t} />)
})

const ToastItem = memo((props: { toast?: Toast }) => {
  if (!props.toast) {
    return null
  }
  const toastWrapperStyle = getToastStyle(props.toast.kind)

  return (
    <Snackbar
      visible={Boolean(props.toast)}
      duration={props.toast?.duration || 3000}
      onDismiss={props.toast?.remove}
      elevation={0}
      wrapperStyle={toastWrapperStyle}
      theme={{
        colors: { inverseSurface: '#fff', inverseOnSurface: '#000' }
      }}
      style={{
        backgroundColor: 'transparent',
        padding: 0,
        margin: 0
      }}
      icon={'Check--enclosed'}
    >
      <Pressable onPress={props.toast?.remove}>
        <DS.Frame row justifyContent="center" gap={'12'} style={props.toast.kind === 'success' && styles.textContainer}>
          {props.toast?.icon && (
            <Image source={{ uri: props.toast.icon }} style={{ width: 32, height: 32 }} width={32} height={32} />
          )}
          {!props.toast?.icon && props.toast.kind === 'success' && (
            <DS.Icon name="Check--enclosed" size={16} color="Utility/Success" />
          )}

          <DS.Text variant="Body Small/Medium">{props.toast.message}</DS.Text>
        </DS.Frame>
      </Pressable>
    </Snackbar>
  )
})

const getToastStyle = (toastKind: ToastKind) => {
  switch (toastKind) {
    case 'success':
      return {
        ...styles.toastSuccess,
        ...styles.toast
      }
    default:
      return {
        ...styles.toastDefault,
        ...styles.toast
      }
  }
}

const styles = StyleSheet.create({
  toast: {
    width: 'auto',
    //
    backgroundColor: 'white',
    //
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 8
    },
    shadowOpacity: 0.46,
    shadowRadius: 11.14,
    elevation: 5
  },
  toastSuccess: {
    position: 'absolute',
    top: 10,
    alignSelf: 'center',
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 24,
    minWidth: 240,
    minHeight: 34
  },
  toastDefault: {
    bottom: 120,
    alignSelf: 'center',
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 50,
    minWidth: 139,
    minHeight: 34
  },
  textContainer: {
    transform: Platform.OS === 'ios' ? [{ translateY: -5 }] : [{ translateY: 0 }]
  }
})
